import { css } from 'emotion';
import { ArrowUp } from 'phosphor-react';
import { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Box, Button } from '../shared-components';
import { Text, textTheme } from '../shared-components/text';
import { colors } from '../shared-components/styles';
import { sendMessage } from './api/chat';
import { useAssistantChat } from './context/AssistantChatProvider';
import { SENDER_ASSISTANT, SENDER_USER } from './helpers/assistant';

export const AssistantChatInput = () => {
  // Importing the context to manage chat history and messages.
  const { guide, threadId, isLoading, addMessage } = useAssistantChat();

  // Ref to manage the textarea element.
  const textareaRef = useRef(null);

  // State to manage the message input.
  const [message, setMessage] = useState('');
  const [disabled, setDisabled] = useState(false);

  const handleSendMessage = useMutation(() => sendMessage({ guide, threadId, message }), {
    onMutate: () => {
      // Add the message to the chat history.
      addMessage({
        sender: SENDER_USER,
        text: message,
      });

      // Reset message state.
      setMessage('');

      // Disable the textarea to prevent multiple submissions.
      setDisabled(true);

      // Reset the textarea height.
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    onSuccess: ({ ResponseMessage }) => {
      // Add the assistant's response to the chat history.
      addMessage({
        sender: SENDER_ASSISTANT,
        text: ResponseMessage,
      });

      // Re-enable the textarea after the message is sent.
      setDisabled(false);
    },
    onError: (error) => {
      console.error('Error sending message:', error);
    },
  });

  // Adjusts the height of the textarea to fit its content dynamically.
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset the height to auto to shrink if needed.
    textarea.style.height = 'auto';

    // Get the computed line height, fallback to 20px if not available.
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 20;

    // Calculate the new height based on scrollHeight, ensuring at least one line.
    const newHeight = Math.max(textarea.scrollHeight - lineHeight, lineHeight);

    // Set the textarea height to fit the content.
    textarea.style.height = `${newHeight}px`;
  };

  // Handler for form submission.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message) return;
    handleSendMessage.mutate();
  };

  // Handler for textarea keydown.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {disabled && (
        <Box
          css={`
            padding-bottom: 12px;
          `}
        >
          <Text helper color={colors.gray[400]}>
            Thinking...
          </Text>
        </Box>
      )}
      <Box
        css={`
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 12px;
          background-color: ${disabled ? colors.gray[100] : colors.white};
          border: 1px solid ${colors.gray[300]};
          border-radius: 8px;
        `}
      >
        <textarea
          ref={textareaRef}
          disabled={isLoading || disabled}
          autoFocus
          rows={1}
          className={css`
            background-color: transparent;
            padding: 12px;
            max-height: 168px;
            width: 100%;
            border: none;
            outline: none;
            resize: none;
            overflow: hidden;
            ${textTheme?.label}

            ::placeholder {
              color: ${colors.gray[400]};
            }
          `}
          placeholder="Ask a question..."
          onInput={(e) => {
            setMessage(e.currentTarget.value);
            adjustTextareaHeight();
          }}
          onKeyDown={handleKeyDown}
          value={message}
        />
        <Box
          css={`
            display: flex;
            justify-content: flex-end;
            padding: 12px;
            width: 100%;
            gap: 4px;
          `}
        >
          <Button
            styles="icon"
            css={`
              width: 32px;
              height: 32px;
              padding: 4px;
              background: ${colors.gray[200]};
              border: 0.5px solid ${colors.gray[200]};
              border-radius: 5px;
            `}
            disabled={!message}
            type="submit"
          >
            <ArrowUp size={16} weight="bold" />
          </Button>
        </Box>
      </Box>
    </form>
  );
};
